import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  Shield, CheckCircle, XCircle, AlertTriangle, AlertOctagon,
  Download, FileText, ArrowRight, Loader2, ExternalLink, 
  QrCode, Home, Info, Activity, Zap, Eye, TrendingUp, 
  Clock, Globe, Mail, Phone, Wallet, User, Building,
  ChevronRight, AlertCircle, ShieldCheck, ShieldAlert, ShieldX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Normalize entity display: convert slug format "www-example-com" to "www.example.com"
const formatEntityDisplay = (entity, entityType) => {
  if (!entity) return entity;
  if (['domain', 'website', 'url'].includes(entityType) && entity.includes('-') && !entity.includes('.')) {
    return entity.replace(/-/g, '.');
  }
  return entity;
};

// Get icon for entity type
const getEntityIcon = (type) => {
  const icons = {
    domain: Globe,
    email: Mail,
    phone: Phone,
    whatsapp: Phone,
    crypto: Wallet,
    crypto_wallet: Wallet,
    social: User,
    social_media: User,
    company: Building
  };
  return icons[type] || Globe;
};

// Get verdict configuration - GREEN/AMBER/RED system
const getVerdictConfig = (verdict, score) => {
  const v = (verdict || '').toUpperCase();
  
  // RED: High risk (score >= 65 or verdict is RED)
  if (v === 'RED' || v.includes('HIGH') || v.includes('CRITICAL') || score >= 65) {
    return {
      icon: ShieldX,
      color: 'red',
      bgClass: 'bg-red-500/10 border-red-500/40',
      textClass: 'text-red-400',
      badgeClass: 'bg-red-600 text-white',
      barClass: 'bg-red-500',
      label: 'RED',
      sublabel: 'High Risk'
    };
  }
  
  // AMBER: Caution needed (score 30-64 or verdict is AMBER/SUSPICIOUS)
  if (v === 'AMBER' || v === 'amber' || v.includes('SUSPICIOUS') || v.includes('CAUTION') || (score >= 40 && score <= 64)) {
    return {
      icon: ShieldAlert,
      color: 'amber',
      bgClass: 'bg-amber-500/10 border-amber-500/40',
      textClass: 'text-amber-400',
      badgeClass: 'bg-amber-500 text-black',
      barClass: 'bg-amber-500',
      label: 'AMBER',
      sublabel: 'Caution Advised'
    };
  }
  
  // GREEN: Low risk (score < 30 or verdict is GREEN/LOW)
  return {
    icon: ShieldCheck,
    color: 'green',
    bgClass: 'bg-green-500/10 border-green-500/40',
    textClass: 'text-green-400',
    badgeClass: 'bg-green-600 text-white',
    barClass: 'bg-green-500',
    label: 'GREEN',
    sublabel: 'Low Risk'
  };
};

const ScamCheckResultsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  useEffect(() => {
    const processPaymentAndGetResults = async () => {
      console.log('[RESULTS] Page loaded, reading URL params...');
      console.log('[RESULTS] Full URL:', window.location.href);
      console.log('[RESULTS] Search params:', window.location.search);
      
      const sessionId = searchParams.get('session_id');
      
      // Read scan_token from URL first, then localStorage fallback
      let scanToken = searchParams.get('scan_token');
      console.log('[RESULTS] scan_token from URL:', scanToken);
      
      // Check for invalid string values
      if (!scanToken || scanToken === 'null' || scanToken === 'undefined' || scanToken === '') {
        console.log('[RESULTS] URL scan_token invalid, checking localStorage...');
        scanToken = sessionStorage.getItem('scan_token');
        console.log('[RESULTS] scan_token from localStorage:', scanToken);
      }
      
      // Read entity from URL first, then localStorage fallback
      let entity = searchParams.get('entity');
      if (!entity || entity === 'null' || entity === 'undefined') {
        entity = sessionStorage.getItem('scan_entity');
        console.log('[RESULTS] entity from localStorage:', entity);
      }
      
      let entityType = searchParams.get('entity_type');
      if (!entityType || entityType === 'null' || entityType === 'undefined') {
        entityType = sessionStorage.getItem('scan_entity_type') || 'domain';
      }
      
      let plan = searchParams.get('plan');
      if (!plan || plan === 'null' || plan === 'undefined') {
        plan = sessionStorage.getItem('scan_plan') || 'scam_check_basic';
      }
      
      // Debug logging
      console.log('[RESULTS] Final values:');
      console.log('[RESULTS]   SESSION ID:', sessionId);
      console.log('[RESULTS]   SCAN TOKEN:', scanToken);
      console.log('[RESULTS]   ENTITY:', entity);
      console.log('[RESULTS]   ENTITY TYPE:', entityType);
      console.log('[RESULTS]   PLAN:', plan);

      if (!sessionId) {
        console.error('[RESULTS] No session_id found');
        setError('No payment session found. Please complete payment first.');
        setLoading(false);
        return;
      }
      
      // Check if scan_token is missing
      if (!scanToken || scanToken === 'null' || scanToken === 'undefined') {
        console.error('[RESULTS] No valid scan_token found');
        setError('Missing scan token. Please try your scam check again from the beginning.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Build query string - scan_token is required
        let queryParams = `session_id=${encodeURIComponent(sessionId)}&scan_token=${encodeURIComponent(scanToken)}`;
        if (entity) queryParams += `&entity=${encodeURIComponent(entity)}`;
        queryParams += `&entity_type=${encodeURIComponent(entityType)}`;
        queryParams += `&plan=${encodeURIComponent(plan)}`;
        
        const apiUrl = `${API_URL}/api/scam-check/process-payment?${queryParams}`;
        console.log('[RESULTS] API REQUEST:', apiUrl);
        
        const response = await fetch(apiUrl);
        console.log('[RESULTS] API Response status:', response.status);

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          console.error('[RESULTS] API Error:', errData);
          throw new Error(errData.detail || 'Failed to process payment');
        }

        const data = await response.json();
        console.log('[RESULTS] API Success, verdict:', data.verdict);
        
        if (data.success) {
          setResults(data);
          toast.success('Analysis complete! Your report is ready.');
          
          // Clear localStorage after successful retrieval
          sessionStorage.removeItem('scan_token');
          sessionStorage.removeItem('scan_entity');
          sessionStorage.removeItem('scan_entity_type');
          sessionStorage.removeItem('scan_plan');
          console.log('[RESULTS] localStorage cleared');
        } else {
          throw new Error('Failed to generate analysis');
        }

      } catch (err) {
        console.error('[RESULTS] Processing error:', err);
        setError(err.message || 'Failed to process your request. Please contact support.');
        toast.error('Error processing request');
      } finally {
        setLoading(false);
      }
    };

    processPaymentAndGetResults();
  }, [searchParams]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] pt-24 pb-16 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="relative mb-8">
            <div className="w-24 h-24 border-4 border-[#1e40af]/30 border-t-[#C3A86B] rounded-full animate-spin mx-auto"></div>
            <Shield className="w-10 h-10 text-[#C3A86B] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Verifying Payment</h2>
          <p className="text-gray-400 mb-2">Running fraud intelligence analysis...</p>
          <p className="text-gray-500 text-sm">This may take a few moments</p>
          
          <div className="mt-8 space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span>Payment verification</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin text-[#C3A86B]" />
              <span>Analyzing entity...</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 opacity-50">
              <Clock className="w-4 h-4" />
              <span>Generating report</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#0a1628] pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-red-500/10 border-red-500/30">
              <CardContent className="p-8 text-center">
                <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-4">Unable to Process Request</h2>
                <p className="text-gray-400 mb-6">{error}</p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button 
                    onClick={() => navigate('/check')}
                    className="bg-[#C3A86B] hover:bg-[#b39759] text-black font-semibold"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button 
                    onClick={() => window.location.reload()}
                    variant="outline"
                    className="border-gray-600 text-gray-300 hover:bg-gray-800"
                  >
                    Refresh Page
                  </Button>
                </div>
                <p className="text-gray-500 text-sm mt-6">
                  Need help? Email{' '}
                  <a href="mailto:info@actionfraudone.com" className="text-[#C3A86B] hover:underline">
                    info@actionfraudone.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Results state
  const verdictConfig = results ? getVerdictConfig(results.verdict, results.risk_score) : null;
  const VerdictIcon = verdictConfig?.icon || Shield;
  const EntityIcon = results ? getEntityIcon(results.entity_type) : Globe;

  return (
    <div className="min-h-screen bg-[#0a1628] pt-24 pb-16" data-testid="scam-results-page">
      <Helmet>
        <title>Fraud Intelligence Report | ActionFraudOne</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* Success Banner */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-sm font-medium">Payment Verified</span>
            </div>
            {results?.analysis_type === 'Advanced' && (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/30 rounded-full">
                <Zap className="w-4 h-4 text-purple-400" />
                <span className="text-purple-400 text-sm font-medium">Advanced Analysis</span>
              </div>
            )}
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Fraud Intelligence Report
            </h1>
            {results?.report_id && (
              <p className="text-gray-400">
                Report ID: <span className="text-[#C3A86B] font-mono">{results.report_id}</span>
              </p>
            )}
          </div>

          {results && (
            <>
              {/* Main Verdict Card */}
              <Card className={`${verdictConfig.bgClass} border-2 mb-8`} data-testid="verdict-card">
                <CardContent className="p-6 md:p-8">
                  {/* Verdict Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
                    <div className="flex-1">
                      {/* Verdict Badge - GREEN/AMBER/RED */}
                      <div className="flex items-center gap-3 mb-4">
                        <span className={`inline-block px-5 py-2.5 ${verdictConfig.badgeClass} text-lg font-bold rounded-lg shadow-lg`}>
                          {verdictConfig.label}
                        </span>
                        <span className={`text-sm font-medium ${verdictConfig.textClass}`}>
                          {verdictConfig.sublabel}
                        </span>
                      </div>
                      <h2 className="text-xl font-semibold text-white mb-2">Entity Analysed</h2>
                      <div className="flex items-center gap-2 mb-1">
                        <EntityIcon className="w-5 h-5 text-gray-400" />
                        <p className="text-gray-300 text-lg break-all">{formatEntityDisplay(results.entity, results.entity_type)}</p>
                      </div>
                      <p className="text-gray-500 text-sm capitalize">{results.entity_type?.replace('_', ' ')}</p>
                    </div>
                    <div className={`w-28 h-28 ${verdictConfig.bgClass} border-2 ${verdictConfig.textClass.replace('text-', 'border-')} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                      <VerdictIcon className={`w-14 h-14 ${verdictConfig.textClass}`} />
                    </div>
                  </div>

                  {/* Risk Score Bar */}
                  <div className="mb-6" data-testid="risk-score">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400 font-medium">Risk Score</span>
                      <span className={`font-bold text-3xl ${verdictConfig.textClass}`}>
                        {results.risk_score}/100
                      </span>
                    </div>
                    <div className="h-4 bg-[#0a1628] rounded-full overflow-hidden border border-gray-700">
                      <div 
                        className={`h-full ${verdictConfig.barClass} rounded-full transition-all duration-1000 ease-out`}
                        style={{ width: `${results.risk_score}%` }}
                      />
                    </div>
                  </div>

                  {/* Verdict Detail */}
                  <div className="bg-[#0a1628]/70 rounded-xl p-5 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className={`w-5 h-5 ${verdictConfig.textClass}`} />
                      <span className="text-white font-semibold text-lg">Verdict</span>
                    </div>
                    <p className="text-gray-300 leading-relaxed">{results.verdict_detail}</p>
                  </div>

                  {/* Risk Factors */}
                  {results.risk_factors && results.risk_factors.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                        Risk Factors Detected ({results.risk_factors.length})
                      </h3>
                      <div className="space-y-2">
                        {results.risk_factors.map((factor, index) => {
                          const sevColor = factor.severity === 'critical' || factor.severity === 'high' 
                            ? 'text-red-400 bg-red-500/10 border-red-500/30' 
                            : factor.severity === 'medium'
                            ? 'text-amber-400 bg-amber-500/10 border-amber-500/30'
                            : 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
                          
                          return (
                            <div key={index} className={`flex items-start gap-3 p-3 ${sevColor} border rounded-lg`}>
                              <AlertOctagon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-sm text-gray-200">{factor.description}</p>
                                <p className="text-xs text-gray-500 mt-1">Source: {factor.source}</p>
                              </div>
                              <span className="text-xs font-semibold uppercase px-2 py-0.5 rounded bg-black/20">
                                {factor.severity}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Positive Signals */}
                  {results.positive_signals && results.positive_signals.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        Positive Signals ({results.positive_signals.length})
                      </h3>
                      <div className="space-y-2">
                        {results.positive_signals.map((signal, index) => (
                          <div key={index} className="flex items-start gap-3 p-3 text-green-400 bg-green-500/10 border border-green-500/30 rounded-lg">
                            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm text-gray-200">{signal.description}</p>
                              <p className="text-xs text-gray-500 mt-1">Source: {signal.source}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendation */}
                  <div className={`p-5 ${verdictConfig.bgClass} border-2 rounded-xl`}>
                    <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                      <Info className="w-5 h-5" />
                      Recommended Action
                    </h3>
                    <p className="text-gray-200 font-medium">{results.recommendation}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Download Section */}
              <Card className="bg-gradient-to-r from-[#0d1d32] to-[#0a1628] border-[#C3A86B]/30 mb-8">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-[#C3A86B]/10 rounded-xl flex items-center justify-center">
                        <FileText className="w-7 h-7 text-[#C3A86B]" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-lg">Premium PDF Report</h3>
                        <p className="text-gray-400 text-sm">Professional fraud intelligence document</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => window.open(`${API_URL}${results.pdf_download_url}`, '_blank')}
                      className="bg-[#C3A86B] hover:bg-[#b39759] text-black font-bold px-6 py-6"
                      data-testid="download-btn"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Report
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Report Details */}
              <Card className="bg-[#0d1d32] border-[#1e40af]/30 mb-8">
                <CardContent className="p-6">
                  <h3 className="text-white font-semibold mb-4">Report Details</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Report ID</p>
                      <p className="text-[#C3A86B] font-mono">{results.report_id}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Generated</p>
                      <p className="text-gray-300">
                        {results.analysis_timestamp ? new Date(results.analysis_timestamp).toLocaleString() : 'Just now'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Analysis Type</p>
                      <p className="text-gray-300">{results.analysis_type}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Sources</p>
                      <p className="text-gray-300">{results.sources_checked?.length || 0} checked</p>
                    </div>
                  </div>
                  
                  {/* Sources */}
                  {results.sources_checked && results.sources_checked.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-[#1e40af]/20">
                      <p className="text-gray-500 text-sm mb-2">Intelligence Sources:</p>
                      <div className="flex flex-wrap gap-2">
                        {results.sources_checked.map((source, i) => (
                          <span key={i} className="px-3 py-1 bg-[#1e40af]/10 border border-[#1e40af]/20 rounded-full text-xs text-gray-400">
                            {source}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Verification */}
              <Card className="bg-[#0d1d32] border-[#1e40af]/30 mb-8">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <QrCode className="w-8 h-8 text-[#C3A86B]" />
                      <div>
                        <h3 className="text-white font-semibold">Verify This Report</h3>
                        <p className="text-gray-400 text-sm">
                          Visit <span className="text-[#C3A86B]">actionfraudone.com/verify-report</span>
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => window.open(`${window.location.origin}/verify-report?id=${results.report_id}`, '_blank')}
                      variant="outline"
                      className="border-[#1e40af]/50 text-gray-300 hover:bg-[#1e40af]/10"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Verify
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Trust & Authority */}
              <p className="text-center text-gray-500 text-xs mb-6" data-testid="trust-line">
                Used by investigators, legal teams, and due diligence professionals
              </p>

              {/* Urgency / Retention Notice */}
              <div className="text-center mb-8">
                <p className="text-gray-500 text-xs" data-testid="expiry-notice">
                  Reports expire after 7 days. Re-check recommended for ongoing risk monitoring.
                </p>
              </div>

              {/* Upsell Block */}
              <Card className="bg-[#0d1d32] border-[#C3A86B]/30 mb-8" data-testid="upsell-block">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold text-lg mb-3">Want deeper verification?</h3>
                  <ul className="space-y-2 mb-5">
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <ChevronRight className="w-4 h-4 text-[#C3A86B] flex-shrink-0" />
                      Ownership tracing
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <ChevronRight className="w-4 h-4 text-[#C3A86B] flex-shrink-0" />
                      Scam network detection
                    </li>
                    <li className="flex items-center gap-2 text-gray-300 text-sm">
                      <ChevronRight className="w-4 h-4 text-[#C3A86B] flex-shrink-0" />
                      Financial risk profiling
                    </li>
                  </ul>
                  <Button
                    onClick={() => navigate('/pricing')}
                    className="bg-[#C3A86B] hover:bg-[#b39759] text-[#0a1628] font-semibold w-full"
                    data-testid="upsell-upgrade-btn"
                  >
                    Upgrade to Advanced Report ({'\u00A3'}49)
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>

              {/* Need Professional Investigation (high risk only) */}
              {results.risk_score >= 40 && (
                <Card className="bg-gradient-to-r from-purple-500/10 to-[#1e40af]/10 border-purple-500/30">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-white font-bold text-lg mb-1">Need Professional Investigation?</h3>
                        <p className="text-gray-400 text-sm">
                          Our expert team can conduct a comprehensive fraud investigation with legal documentation.
                        </p>
                      </div>
                      <Button 
                        onClick={() => navigate('/investigations')}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-semibold flex-shrink-0"
                      >
                        View Services
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Footer Links */}
          <div className="text-center mt-8">
            <Link to="/check" className="text-[#C3A86B] hover:text-[#b39759] text-sm font-medium">
              Run Another Check
            </Link>
            <span className="text-gray-600 mx-3">|</span>
            <Link to="/" className="text-gray-400 hover:text-gray-300 text-sm">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScamCheckResultsPage;
